import { apiService } from "api/apiService";
import type {
  TeacherPortfolioTaskTemplateShallowResource,
  PortfolioTaskTemplateResource,
  FilterResource,
} from "api/generated";
import {
  teacherLibraryTaskFromDto,
  libraryTaskFromDto,
} from "portfolio/api/mappers";
import type { TeacherLibraryTask, LibraryTask, TaskDto } from "portfolio/types";
import type { PaginatedResponse, PaginatedResult } from "api/types";
import type { Filter } from "shared/types";
import { mapPaginatedResponse } from "api/mappers/pagination.mapper";
import { mapFilters } from "shared/mappers";

export const fetchLibraryTasks = async (
  params: Record<string, string> = {},
): Promise<PaginatedResult<TeacherLibraryTask>> => {
  const { data } = await apiService.get<
    PaginatedResponse<TeacherPortfolioTaskTemplateShallowResource>
  >("api/portfolio/tasks/library", { params });

  return mapPaginatedResponse(data, teacherLibraryTaskFromDto);
};

export const fetchLibraryTasksFilters = async (): Promise<Filter[]> => {
  const {
    data: { data: filters },
  } = await apiService.get<{ data: FilterResource[] }>(
    "api/portfolio/tasks/library/filters",
  );

  return mapFilters(filters);
};

/**
 * Fetch a library task (template_task) by ID
 * Endpoint: GET /portfolio/template-tasks/{portfolioTaskTemplate}
 */
export const fetchLibraryTaskById = async (
  taskId: number,
): Promise<LibraryTask> => {
  const { data: task } = await apiService.get<PortfolioTaskTemplateResource>(
    `api/portfolio/template-tasks/${taskId}`,
  );

  return libraryTaskFromDto(task);
};

export const createLibraryTask = async (dto: TaskDto): Promise<LibraryTask> => {
  const { data: task } = await apiService.post<PortfolioTaskTemplateResource>(
    "api/portfolio/template-tasks",
    dto,
  );

  return libraryTaskFromDto(task);
};

export const updateLibraryTask = async (
  taskId: number,
  dto: TaskDto,
): Promise<LibraryTask> => {
  const { data: task } = await apiService.put<PortfolioTaskTemplateResource>(
    `api/portfolio/template-tasks/${taskId}`,
    dto,
  );

  return libraryTaskFromDto(task);
};

export const deleteLibraryTask = async (taskId: number): Promise<void> => {
  await apiService.delete(`api/portfolio/template-tasks/${taskId}`);
};

export const createLibraryTaskAttachments = async (
  taskId: number,
  files: File[],
  onUploadProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<LibraryTask> => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append("attachments[]", file);
  });

  const { data: task } = await apiService.post<PortfolioTaskTemplateResource>(
    `api/portfolio/template-tasks/${taskId}/attachments`,
    formData,
    {
      signal,
      onUploadProgress: (event) => {
        if (!onUploadProgress || !event.total) {
          return;
        }
        onUploadProgress(Math.round((event.loaded / event.total) * 100));
      },
    },
  );

  return libraryTaskFromDto(task);
};

export const uploadLibraryTaskBanner = async (
  taskId: number,
  file: File,
): Promise<{ message: string }> => {
  const formData = new FormData();
  formData.append("banner_image", file);

  const { data } = await apiService.post<{ message: string }>(
    `api/portfolio/template-tasks/${taskId}/banner`,
    formData,
  );

  return data;
};

export const deleteLibraryTaskBanner = async (
  taskId: number,
): Promise<{ message: string }> => {
  const { data } = await apiService.delete<{ message: string }>(
    `api/portfolio/template-tasks/${taskId}/banner`,
  );

  return data;
};

export const duplicateLibraryTask = async (
  taskId: number,
): Promise<LibraryTask> => {
  const { data: task } = await apiService.post<PortfolioTaskTemplateResource>(
    `api/portfolio/template-tasks/duplicate/${taskId}`,
  );

  return libraryTaskFromDto(task);
};

// --- Library Task Owners ---

export const addLibraryTaskOwners = async (
  taskId: number,
  ownerIds: number[],
): Promise<LibraryTask> => {
  const {
    data: { data: task },
  } = await apiService.post<{ data: PortfolioTaskTemplateResource }>(
    `api/portfolio/template-tasks/${taskId}/owners/add`,
    { owners: ownerIds },
  );

  return libraryTaskFromDto(task);
};

export const removeLibraryTaskOwner = async (
  taskId: number,
  ownerId: number,
): Promise<LibraryTask> => {
  const {
    data: { data: task },
  } = await apiService.delete<{ data: PortfolioTaskTemplateResource }>(
    `api/portfolio/template-tasks/${taskId}/owners/${ownerId}/remove`,
  );

  return libraryTaskFromDto(task);
};

export const copyScheduledTaskToLibrary = async (
  scheduledTaskId: number,
): Promise<LibraryTask> => {
  const { data: task } = await apiService.post<PortfolioTaskTemplateResource>(
    `api/portfolio/template-tasks/from-task/${scheduledTaskId}`,
  );

  return libraryTaskFromDto(task);
};
